import React, { useState, useEffect } from 'react';
import {
  getAccount,
  updateAccount,
  deleteAccount,
  addAccountMember,
  updateAccountMember,
  removeAccountMember,
  createAccount,
  listAccounts,
} from '../services/apiClient';
import {
  Users,
  Settings,
  Trash2,
  UserPlus,
  Crown,
  User,
  Eye,
  X,
  Check,
  ChevronLeft,
  Plus,
  Building2,
  Edit2,
  AlertTriangle,
} from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import './AccountManagement.css';

function AccountManagement({ accountId, onBack, onAccountChange, onAccountCreated, embedded = false, openCreateModal = false, suggestedAccountName = null }) {
  const { logout } = useAuth();
  const [account, setAccount] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal states
  const [showAddMember, setShowAddMember] = useState(false);
  const [showCreateAccount, setShowCreateAccount] = useState(openCreateModal);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEditName, setShowEditName] = useState(false);
  
  // Form states
  const [newMemberEmail, setNewMemberEmail] = useState('');
  const [newMemberRole, setNewMemberRole] = useState('user');
  const [newAccountName, setNewAccountName] = useState(suggestedAccountName || '');
  const [editedName, setEditedName] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (accountId) {
      loadAccount();
    } else {
      // No accountId - stop loading
      setLoading(false);
    }
  }, [accountId]);

  // Respond to openCreateModal prop changes
  useEffect(() => {
    if (openCreateModal) {
      setShowCreateAccount(true);
      // If no accountId, stop loading immediately to show the modal
      if (!accountId) {
        setLoading(false);
      }
    }
  }, [openCreateModal, accountId]);

  // Update account name when suggestedAccountName is provided or modal opens
  useEffect(() => {
    if (showCreateAccount) {
      // Always set to suggested name if available, otherwise keep current value or use placeholder
      if (suggestedAccountName) {
        setNewAccountName(suggestedAccountName);
      } else if (!newAccountName) {
        // Set a default if no suggested name and field is empty
        setNewAccountName('My Trading Account');
      }
    }
  }, [suggestedAccountName, showCreateAccount]);

  const loadAccount = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAccount(accountId);
      setAccount(data.account);
      setUserRole(data.userRole);
      setEditedName(data.account.name);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load account');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    try {
      setActionLoading(true);
      await addAccountMember(accountId, newMemberEmail, newMemberRole);
      setShowAddMember(false);
      setNewMemberEmail('');
      setNewMemberRole('user');
      loadAccount();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add member');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateMemberRole = async (memberId, newRole) => {
    try {
      setActionLoading(true);
      await updateAccountMember(accountId, memberId, newRole);
      loadAccount();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update member role');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this member?')) return;
    try {
      setActionLoading(true);
      await removeAccountMember(accountId, memberId);
      loadAccount();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove member');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateName = async (e) => {
    e.preventDefault();
    try {
      setActionLoading(true);
      await updateAccount(accountId, { name: editedName });
      setShowEditName(false);
      loadAccount();
      if (onAccountChange) {
        onAccountChange();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update account name');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setActionLoading(true);
      await deleteAccount(accountId);
      setShowDeleteConfirm(false);
      if (onBack) {
        onBack();
      }
      if (onAccountChange) {
        onAccountChange();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete account');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCloseCreateModal = () => {
    setShowCreateAccount(false);
    // Reset to suggested name if available, otherwise empty
    setNewAccountName(suggestedAccountName || '');
    
    // If this is a new user with no account (signup flow), redirect to login
    if (!accountId && openCreateModal) {
      logout();
    }
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    
    // Manual validation
    const accountName = newAccountName.trim();
    if (!accountName) {
      setError('Account name is required');
      return;
    }
    
    try {
      setActionLoading(true);
      setError(null); // Clear any previous errors
      const data = await createAccount(accountName);
      setShowCreateAccount(false);
      setNewAccountName(suggestedAccountName || '');
      if (onAccountCreated) {
        onAccountCreated(data.account);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create account');
    } finally {
      setActionLoading(false);
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <Crown size={16} className="role-icon admin" />;
      case 'user':
        return <User size={16} className="role-icon user" />;
      case 'viewer':
        return <Eye size={16} className="role-icon viewer" />;
      default:
        return <User size={16} className="role-icon" />;
    }
  };

  const isAdmin = userRole === 'admin';

  if (loading) {
    return (
      <div className="account-management">
        <div className="loading-state">Loading account details...</div>
      </div>
    );
  }

  return (
    <div className={`account-management ${embedded ? 'embedded' : ''}`}>
      {/* Header */}
      <div className="account-management-header">
        {!embedded && (
          <button className="btn-back" onClick={onBack}>
            <ChevronLeft size={18} />
            <span>Back</span>
          </button>
        )}
        {!embedded && <h1>Account Management</h1>}
        <button 
          className="btn-action"
          onClick={() => setShowCreateAccount(true)}
        >
          <Plus size={16} />
          <span>New Account</span>
        </button>
      </div>

      {error && (
        <div className="error-banner">
          <AlertTriangle size={16} />
          <span>{error}</span>
          <button onClick={() => setError(null)}>
            <X size={16} />
          </button>
        </div>
      )}

      {account && (
        <>
          {/* Account Info Card */}
          <div className="account-card">
            <div className="account-card-header">
              <Building2 size={24} />
              <div className="account-info">
                {showEditName ? (
                  <form onSubmit={handleUpdateName} className="edit-name-form">
                    <input
                      type="text"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      autoFocus
                    />
                    <button type="submit" disabled={actionLoading}>
                      <Check size={16} />
                    </button>
                    <button type="button" onClick={() => setShowEditName(false)}>
                      <X size={16} />
                    </button>
                  </form>
                ) : (
                  <h2>
                    {account.name}
                    {isAdmin && (
                      <button 
                        className="edit-name-button"
                        onClick={() => setShowEditName(true)}
                      >
                        <Edit2 size={14} />
                      </button>
                    )}
                  </h2>
                )}
                <span className="account-id">ID: {account.id}</span>
              </div>
              {isAdmin && (
                <button 
                  className="btn-icon-sm btn-danger-subtle"
                  onClick={() => setShowDeleteConfirm(true)}
                  title="Delete Account"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
            <div className="account-meta">
              <span>Created: {new Date(account.created_at).toLocaleDateString()}</span>
              <span>Your role: <strong>{userRole}</strong></span>
            </div>
          </div>

          {/* Members Section */}
          <div className="members-section">
            <div className="section-header">
              <h3>
                <Users size={20} />
                Team Members ({account.members?.length || 0})
              </h3>
              {isAdmin && (
                <button 
                  className="btn-action"
                  onClick={() => setShowAddMember(true)}
                >
                  <UserPlus size={16} />
                  <span>Add Member</span>
                </button>
              )}
            </div>

            <div className="members-list">
              {account.members?.map((member) => (
                <div key={member.id} className="member-card">
                  <div className="member-info">
                    <div className="member-avatar">
                      {member.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="member-details">
                      <span className="member-name">{member.name}</span>
                      <span className="member-email">{member.email}</span>
                    </div>
                  </div>
                  <div className="member-actions">
                    {isAdmin ? (
                      <select
                        value={member.role}
                        onChange={(e) => handleUpdateMemberRole(member.id, e.target.value)}
                        className="role-select"
                        disabled={actionLoading}
                      >
                        <option value="admin">Admin</option>
                        <option value="user">User</option>
                        <option value="viewer">Viewer</option>
                      </select>
                    ) : (
                      <span className={`role-badge ${member.role}`}>
                        {getRoleIcon(member.role)}
                        {member.role}
                      </span>
                    )}
                    {isAdmin && account.members.length > 1 && (
                      <button
                        className="remove-member-button"
                        onClick={() => handleRemoveMember(member.id)}
                        disabled={actionLoading}
                        title="Remove member"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Role Legend */}
          <div className="role-legend">
            <h4>Role Permissions</h4>
            <div className="role-items">
              <div className="role-item">
                <Crown size={16} className="admin" />
                <div>
                  <strong>Admin</strong>
                  <span>Full access: manage members, credentials, and transfers</span>
                </div>
              </div>
              <div className="role-item">
                <User size={16} className="user" />
                <div>
                  <strong>User</strong>
                  <span>Can view balances and execute transfers</span>
                </div>
              </div>
              <div className="role-item">
                <Eye size={16} className="viewer" />
                <div>
                  <strong>Viewer</strong>
                  <span>Read-only access to balances and history</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Add Member Modal */}
      {showAddMember && (
        <div className="modal-overlay" onClick={() => setShowAddMember(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Team Member</h3>
              <button onClick={() => setShowAddMember(false)}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleAddMember}>
              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  value={newMemberEmail}
                  onChange={(e) => setNewMemberEmail(e.target.value)}
                  placeholder="colleague@company.com"
                  required
                />
              </div>
              <div className="form-group">
                <label>Role</label>
                <select
                  value={newMemberRole}
                  onChange={(e) => setNewMemberRole(e.target.value)}
                >
                  <option value="admin">Admin</option>
                  <option value="user">User</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowAddMember(false)}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={actionLoading}>
                  {actionLoading ? 'Adding...' : 'Add Member'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Account Modal */}
      {showCreateAccount && (
        <div className="modal-overlay" onClick={handleCloseCreateModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create New Account</h3>
              <button onClick={handleCloseCreateModal}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateAccount}>
              <div className="form-group">
                <label>Account Name</label>
                <input
                  type="text"
                  value={newAccountName}
                  onChange={(e) => {
                    setNewAccountName(e.target.value);
                    setError(null); // Clear error when user types
                  }}
                  placeholder={suggestedAccountName || "My Trading Account"}
                />
              </div>
              <p className="form-hint">
                A new account starts empty. You'll need to add exchange credentials 
                and configure wallets after creation.
              </p>
              <div className="modal-actions">
                <button type="button" onClick={handleCloseCreateModal}>
                  Cancel
                </button>
                <button type="submit" className="primary" disabled={actionLoading}>
                  {actionLoading ? 'Creating...' : 'Create Account'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal danger" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Account</h3>
              <button onClick={() => setShowDeleteConfirm(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <AlertTriangle size={48} className="warning-icon" />
              <p>
                Are you sure you want to delete <strong>{account?.name}</strong>?
              </p>
              <p className="warning-text">
                This will permanently delete all transfer history, balances, and 
                exchange credentials associated with this account. This action 
                cannot be undone.
              </p>
            </div>
            <div className="modal-actions">
              <button type="button" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </button>
              <button 
                type="button" 
                className="danger" 
                onClick={handleDeleteAccount}
                disabled={actionLoading}
              >
                {actionLoading ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AccountManagement;
